import { useParams } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import { useMediaQuery } from '@mui/material';
import { TOptions } from 'i18next';
import { useTranslation } from 'react-i18next';
import { notUndefined } from '../utils/utilities';

export const useMobileMatch = () => useMediaQuery('(max-width:740px)');

export function useLocalStorage<TemplateValue>(key: string, initialValue: TemplateValue) {
  const [storedValue, setStoredValue] = useState<TemplateValue>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Unable to get ${key} from localstorage:`, error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: TemplateValue | ((storedValue: TemplateValue) => TemplateValue)) => {
      try {
        if (value instanceof Function) {
          setStoredValue((oldValue) => {
            const resValue = value(oldValue);
            window.localStorage.setItem(key, JSON.stringify(resValue));
            return resValue;
          });
        } else {
          setStoredValue(value);
          window.localStorage.setItem(key, JSON.stringify(value));
        }
      } catch (error) {
        console.error(`Unable to save ${key} in localstorage:`, error);
      }
    },
    [setStoredValue, key]
  );

  return [storedValue, setValue] as const;
}

/**
 * @description Хук для преобразования массива объектов в Map.
 * Если array === undefined, вернется пустой Map.
 *
 * @param array - Массив объектов, который необходимо преобразовать в Map.
 * @param key - Имя свойства объекта из массива, которое будет использоваться в качестве идентификатора в Map.
 */
export const useMapFromArray = <DataType extends Record<string, any>, Key extends keyof DataType>(
  array: DataType[] | undefined,
  key: Key
): Map<DataType[Key], DataType> =>
  useMemo(() => new Map(array?.map((item) => [item[key], item]) ?? []), [array, key]);

/**
 * @summary Хук, который преобразовывает параметр из адресной строки в число и возврашает его. В случае, если параметр не является числом, бросает ошибку.
 *
 * @param pathId - Параметр из адресной строки.
 *
 * @param missingAsNan - Пропускать ли проверку на нечисловой параметр.
 *
 * @example
 * const id = useNumericPathId('id', isCreating);
 */
export const useNumericPathId = (pathId: string = 'id', missingAsNan: boolean = false): number => {
  const value = useParams<any>()[pathId];
  if (value === undefined) {
    if (missingAsNan) {
      return NaN;
    }
    throw new Error(`Parameter ${pathId} not found in path.`);
  }
  if (!/^[1-9][0-9]{0,12}$/.test(value)) {
    throw new Error(`Wrong variable ${pathId}=${JSON.stringify(value)} in path.`);
  }
  return parseInt(value, 10);
};

/**
 * @summary Хук, который возвращает функцию для проверки на undefined перевода из функции t. В случае, если перевода нет, бросает ошибку.
 */
export const useDefinedTranslation = () => {
  const { t, i18n } = useTranslation();

  const definedT = useCallback(
    (text: string, options?: TOptions | string) => notUndefined(t(text, options)),
    [t]
  );

  return { t: definedT, language: i18n.language } as const;
};

export { useMessagesPagination } from './useMessagesPagination';
